import React, { useEffect, useState } from 'react';
import { Liveblog, ILiveblog, INewPosts, AcceptedCookiesConfig } from '@sky-it-cmscomposer/liveblog-design-system';
import { liveblogContainerWidget, getPageFromUrl } from '@sky-it-cmscomposer/liveblog-widgets';

import skyCMP from '@sky-uk/ita-js-common-cmp';
import { IModelWrapper } from '../../typings/common-types';
import { useService } from '../hooks/useService';
import { useDateNow } from '../hooks/useDateNow';
import { getBaseUrl, goToUrl, setDocumentTitle, smoothScrollToTop } from '../../utils/page-utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { vendorsConfigs } from '../../utils/cmp-vendors';
import { isAuthorMode, isLocalDomain } from '../../utils/aem-utils';
import { isEmptyObject } from '../../utils/props-validator';

interface ILiveblogContainer extends IModelWrapper<ILiveblog> {
    isWebView?: boolean;
}

const LiveblogContainer: React.FC<ILiveblogContainer> = ({ query, initialModel, live, isWebView, config }) => {
    const [newPostsBadgeCounter, setNewPostsBadgeCounter] = useState<number>(0);
    const [newPostsLabelCounter, setNewPostsLabelCounter] = useState<number>(0);

    const [acceptedCookiesConfig, setAcceptedCookiesConfig] = useState<AcceptedCookiesConfig>({});

    const { dateNow } = useDateNow();
    const { pageTitle } = usePageTitle();
    const { model } = useService({
        live,
        query,
        widget: liveblogContainerWidget,
        initialValue: initialModel,
        setNewPostsBadgeCounter,
        setNewPostsLabelCounter,
    });
    const hasInitialModel = !isEmptyObject(initialModel);
    const isLoading = isEmptyObject(model);

    // Listen to CMP changes on vendors (skip it on local env, author mode on AEM & web-view)
    useEffect(() => {
        vendorsConfigs.forEach((vendorConfig) => {
            if (isLocalDomain() || isAuthorMode() || isWebView) {
                setAcceptedCookiesConfig((prevCookiesAllowed) => ({
                    ...prevCookiesAllowed,
                    [vendorConfig.name]: true,
                }));
            } else {
                skyCMP.onChange(vendorConfig.id, (consent) => {
                    setAcceptedCookiesConfig((prevCookiesAllowed) => ({
                        ...prevCookiesAllowed,
                        [vendorConfig.name]: consent,
                    }));
                });
            }
        });
    }, []);

    const newPostsConfig: INewPosts = {
        badgeCounter: newPostsBadgeCounter,
        labelCounter: newPostsLabelCounter,
        handleBadgeClick: () => {
            if (getPageFromUrl() > 1) {
                goToUrl(getBaseUrl());
            } else {
                smoothScrollToTop();
            }
            setNewPostsBadgeCounter(0);
            setDocumentTitle(pageTitle, 0);
        },
        isBadgeShown: !!newPostsBadgeCounter,
    };

    if (!hasInitialModel && isLoading) {
        return null;
    }

    return (
        <Liveblog
            {...model}
            dateNow={dateNow}
            newPostsConfig={newPostsConfig}
            acceptedCookiesConfig={acceptedCookiesConfig}
            isWebView={isWebView}
        />
    );
};

export default LiveblogContainer;
